import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { Menu } from 'dig-components/menu';
import { Button } from 'dig-components/buttons';
import { Text } from 'dig-components/typography';
import { AccessoryBadge } from 'dig-components/badges';
import { UIIcon } from 'dig-components/icons';
import HfReactHelper from 'hellosign/components/hf-react-helper';
import TextTag, { TextTagType } from 'hellospa/foundation/ui/text-tag';
import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';
import { withRebrandTheme, RebrandTheme } from 'hello-react/components/rebrand';
import FormattedFaxNumber from 'common/components/formatted-fax-number';

import FacebookLogo from './icons/facebook';
import LinkedInLogo from './icons/linkedin';
import TwitterLogo from './icons/twitter';

import BulkSendModal from './bulk-send-modal';
import styles from './global-nav.scss';

export enum NavItemId {
  Home = 'home',
  SignDocuments = 'signdocs',
  SendFax = 'sendfax',
  Templates = 'templates',
  Documents = 'documents',
  ManageTeam = 'manageteam',
  ManageIntegrations = 'manage_integrations',
  TemplateConverter = 'templateconverter',
  GetFreePages = 'getfreepages',
  FilterAllTeam = 'all_team',
  FilterDrafts = 'draft',
  FilterSent = 'sent',
  FilterReceived = 'received',
}

export type HelloFaxFilter = NavItemId.FilterAllTeam
  | NavItemId.FilterDrafts
  | NavItemId.FilterSent
  | NavItemId.FilterReceived;

type SiteCode = typeof HfReactHelper.HfSites.SITE_CODES[number];

type FaxLine = {
  number: string,
};

const ActiveItemContext = React.createContext<NavItemId | null>(NavItemId.SignDocuments);

type NavItemProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  id: NavItemId,
  'data-qa-ref': string,
  children: React.ReactNode,
};

function NavItem({
  id,
  children,
  'data-qa-ref': dataQaRef,
  ...props
}: NavItemProps) {
  const activeItemId = React.useContext(ActiveItemContext);
  const isActive = React.useMemo(() => id === activeItemId, [id, activeItemId]);

  return (
    <div
      className={classnames(styles.navItem, {
        [styles.active]: isActive,
      })}
      data-qa-ref={dataQaRef}
    >
      <a {...props}>
        <Text size="large" className={styles.navItemText}>
          {children}
        </Text>
      </a>
    </div>
  );
}


type FilterProps = {
  siteCode: SiteCode,
  filter: HelloFaxFilter,
  children: React.ReactNode,
  tagType: TextTagType,
  isActive: boolean,
};

function Filter({
  siteCode,
  filter,
  children,
  tagType,
  isActive,
  ...props
}: FilterProps) {
  const site = HfReactHelper.HfSites.getSite(siteCode);
  const url = HfReactHelper.urlHelper(`home/manage?filter=${filter}`, site);

  return (
    <div
      {...props}
      className={classnames(styles.filter, {
        [styles.active]: isActive,
      })}
    >
      <a href={url}>
        <TextTag
          kind={tagType}
        >
          <Text className={styles.filterText}>
            {children}
          </Text>
        </TextTag>
      </a>
    </div>
  );
}

type GlobalNavProps = {
  siteCode: SiteCode,
  canAccessAdminConsole: boolean,
  canAccessBulkSendUI: boolean,
  canAccessTemplateConverter: boolean,
  hasTeamFilter: boolean,
  activeItemId?: NavItemId,

  // App is a complex legacy prop. I don't believe we have
  // any defined types for it. We'll use it with caution
  // here and not make any assumptions about what it can
  // or can't do.
  app: any,
};

function GlobalNav({
  siteCode,
  activeItemId,
  canAccessAdminConsole,
  canAccessBulkSendUI,
  canAccessTemplateConverter,
  hasTeamFilter,
  app,
}: GlobalNavProps) {
  const site = HfReactHelper.HfSites.getSite(siteCode);
  const isSign = HfReactHelper.HfSites.isHelloSign(site);
  const isFax = HfReactHelper.HfSites.isHelloFax(site);

  const currentItemId = React.useMemo(() => {
    if (activeItemId) return activeItemId;

    if (['/', '/home', '/home/index'].includes(window.location.pathname)) {
      return NavItemId.Home;
    }

    return null;
  }, [activeItemId]);

  const isViewingFaxes = React.useMemo(() => {
    if (!currentItemId) return false;

    return [
      NavItemId.Documents,
      NavItemId.FilterAllTeam,
      NavItemId.FilterSent,
      NavItemId.FilterReceived,
      NavItemId.FilterDrafts,
    ].includes(currentItemId);
  }, [currentItemId]);

  const faxLines = React.useMemo<FaxLine[] | null>(() => {
    if (!isFax) return null;

    if ('getFaxLines' in app.user) {
      return app.user.getFaxLines();
    } else {
      return [];
    }
  }, [isFax, app.user]);

  const [isBulkSendModalOpen, setIsBulkSendUpgradeModalOpen] = React.useState(false);

  const onBulkSendPressed = React.useCallback((evt) => {
    if (!canAccessBulkSendUI) {
      // Prevent page navigation if user does not have
      // access to Bulk Send.
      evt.preventDefault();

      // Show upsell modal instead.
      setIsBulkSendUpgradeModalOpen(true);
    }
  }, [canAccessBulkSendUI]);

  const onBulkSendModalRequestClose = React.useCallback(() => {
    setIsBulkSendUpgradeModalOpen(false);
  }, []);

  return (
    <ActiveItemContext.Provider value={currentItemId}>
      <div className={styles.globalNav}>
        <div className={styles.actionButtons}>
          {isSign && (
            <>
              <Button
                variant="primary"
                size="large"
                data-qa-ref="global-nav-sign-documents-btn"
                href={HfReactHelper.urlHelper('prep-and-send', site)}
              >
                <FormattedMessage
                  id=""
                  description="Text for a button which redirects user to the Sign Documents page when pressed."
                  defaultMessage="Sign documents" />
              </Button>
              <Button
                variant="opacity"
                size="large"
                data-qa-ref="global-nav-bulk-send-btn"
                href={HfReactHelper.urlHelper('prep-and-send/new/13', site)}
                onClick={onBulkSendPressed}
              >
                <FormattedMessage
                  id=""
                  description="Text for a button which redirects user to the Sign Documents page when pressed."
                  defaultMessage="Bulk send" />
              </Button>
              {!canAccessBulkSendUI && (
                <BulkSendModal
                  open={isBulkSendModalOpen}
                  onRequestClose={onBulkSendModalRequestClose} />
              )}
            </>
          )}
          {isFax && (
            <>
              <Button
                variant="primary"
                size="large"
                data-qa-ref="global-nav-send-fax-btn"
                href={HfReactHelper.urlHelper('prep-and-send', site)}
              >
                <FormattedMessage
                  id=""
                  description="Text for a button which redirects user to the Send a Fax page when pressed."
                  defaultMessage="Send faxes" />
              </Button>
            </>
          )}
        </div>
        <div className={styles.navItems} data-qa-ref="global-nav-links">
          <NavItem
            id={NavItemId.Home}
            data-qa-ref="global-nav-home-link"
            href="/"
          >
            <FormattedMessage
              id=""
              description="Text for a link which redirects user to the homepage when pressed."
              defaultMessage="Home" />
          </NavItem>
          {isFax && (
            <NavItem
              id={NavItemId.SignDocuments}
              data-qa-ref="global-nav-sign-documents-link"
              href={HfReactHelper.urlHelper('home/request', site)}
            >
              <FormattedMessage
                id=""
                description="Text for a link which redirects user to the page where they can sign documents when pressed."
                defaultMessage="Sign documents" />
            </NavItem>
          )}

          {isSign && (
            <NavItem
              id={NavItemId.Templates}
              data-qa-ref="global-nav-templates-link"
              href={HfReactHelper.urlHelper('home/createReusableDocs', site)}
            >
              <FormattedMessage
                id=""
                description="Text for a link which redirects user to the Templates page when pressed."
                defaultMessage="Templates" />
            </NavItem>
          )}

          <NavItem
            id={NavItemId.Documents}
            data-qa-ref="global-nav-documents-link"
            href={HfReactHelper.urlHelper('home/manage', site)}
          >
            {isSign && (
              <FormattedMessage
                id=""
                description="Text for a link which redirects user to the Documents page when pressed."
                defaultMessage="Documents" />
            )}
            {isFax && (
              <FormattedMessage
                id=""
                description="Text for a link which redirects user to the Faxes page when pressed."
                defaultMessage="Faxes" />
            )}
          </NavItem>

          {isFax && isViewingFaxes && (
            <div className={styles.filtersList} data-qa-ref="global-nav-filter-list">
              {hasTeamFilter && (
                <Filter
                  siteCode={siteCode}
                  filter={NavItemId.FilterAllTeam}
                  tagType={TextTagType.Info}
                  isActive={currentItemId === NavItemId.FilterAllTeam}
                  data-qa-ref="global-nav-filter-all-team"
                >
                  <FormattedMessage
                    id=""
                    description="Text for a link which shows all faxes sent by everyone on the team when pressed."
                    defaultMessage="All team faxes" />
                </Filter>
              )}
              <Filter
                siteCode={siteCode}
                filter={NavItemId.FilterSent}
                tagType={TextTagType.Success}
                isActive={currentItemId === NavItemId.FilterSent}
                data-qa-ref="global-nav-filter-sent"
              >
                <FormattedMessage
                  id=""
                  description="Text for a link which shows all documents sent by the user when pressed."
                  defaultMessage="Sent" />
              </Filter>
              <Filter
                siteCode={siteCode}
                filter={NavItemId.FilterReceived}
                tagType={TextTagType.Info}
                isActive={currentItemId === NavItemId.FilterReceived}
                data-qa-ref="global-nav-filter-received"
              >
                <FormattedMessage
                  id=""
                  description="Text for a link which shows all documents received by the user when pressed."
                  defaultMessage="Received" />
              </Filter>
              <Filter
                siteCode={siteCode}
                filter={NavItemId.FilterDrafts}
                tagType={TextTagType.Neutral}
                isActive={currentItemId === NavItemId.FilterDrafts}
                data-qa-ref="global-nav-filter-drafts"
              >
                <FormattedMessage
                  id=""
                  description="Text for a link which shows all document drafts owned by the user when pressed."
                  defaultMessage="Drafts" />
              </Filter>
            </div>
          )}

          {/* Show Teams link only if user cannot access admin console. */}
          {!canAccessAdminConsole && (
            <NavItem
              id={NavItemId.ManageTeam}
              data-qa-ref="global-nav-teams-link"
              href={HfReactHelper.urlHelper('home/team', site)}
            >
              <FormattedMessage
                id=""
                description="Text for a link which redirects user to the Teams page when pressed."
                defaultMessage="Teams" />
            </NavItem>
          )}

          <NavItem
            id={NavItemId.ManageIntegrations}
            data-qa-ref="global-nav-integrations-link"
            href={HfReactHelper.urlHelper('home/myAccount?current_tab=integrations', site)}
          >
            <FormattedMessage
              id=""
              description="Text for a link which redirects user to the Integrations settings page when pressed."
              defaultMessage="Integrations" />
          </NavItem>

          {/* Show template converter only if the user has access to it. */}
          {/* (Requires the "template conversion" account tag) */}
          {isSign && canAccessTemplateConverter && (
            <NavItem
              id={NavItemId.TemplateConverter}
              data-qa-ref="global-nav-convert-template-link"
              href={HfReactHelper.urlHelper('template/converter', site)}
            >
              <>
                <FormattedMessage
                  id=""
                  description="Text for a link which redirects user to the Template Converter tool when pressed. The template converter converts users' templates from a competitor to HelloSign templates."
                  defaultMessage="Convert template" />
                <AccessoryBadge className={styles.betaTag}>
                  <FormattedMessage
                    id=""
                    description="A tag next to feature which is currently in beta"
                    defaultMessage="Beta" />
                </AccessoryBadge>
              </>
            </NavItem>
          )}

          {isFax && (
            <NavItem
              id={NavItemId.GetFreePages}
              data-qa-ref="global-nav-get-started-link"
              href={HfReactHelper.urlHelper('account/gettingStarted', site)}
            >
              <FormattedMessage
                id=""
                description="Text for a link which redirects user to the Getting Started page when pressed. This page will allow them to share HelloFax to increase their Fax Page quota."
                defaultMessage="Get free pages" />
            </NavItem>
          )}

          {isFax && !!faxLines && faxLines.length > 0 && (
            <div className={styles.faxLines} data-qa-ref="global-nav-fax-lines">
              <Text isBold color="faint">
                <FormattedMessage
                  id=""
                  description="Header text for a list of fax numbers."
                  defaultMessage="Your fax numbers" />
              </Text>
              <Spacer top={SpacerSize.SM} />
              {faxLines.map(({ number }) => (
                <Spacer bottom={SpacerSize.XS} key={number}>
                  <Text data-qa-ref="global-nav-fax-line">
                    <FormattedFaxNumber faxNumber={number} />
                  </Text>
                </Spacer>
              ))}
            </div>
          )}
        </div>
        <div className={styles.footerItems}>
          <div className={styles.footerItem}>
            <Button
              className={styles.navButton}
              variant="transparent"
              href={HfReactHelper.urlHelper('info/pricing', site)}
              data-qa-ref="global-nav-pricing-link"
              target="_blank"
              rel="noreferrer"
              hasNoUnderline
            >
              <Text color="faint">
                <FormattedMessage
                  id=""
                  description="Text for a link which redirects user to the Plans and Pricing page."
                  defaultMessage="Pricing" />
              </Text>
            </Button>
          </div>
          <div className={styles.footerItem}>
            <Menu.Wrapper closeOnSelection>
              {({ getContentProps, getTriggerProps }) => (
                <>
                  <Button
                    {...getTriggerProps()}
                    className={styles.navButton}
                    variant="transparent"
                    data-qa-ref="global-nav-products-menu"
                    hasNoUnderline
                  >
                    <Text color="faint">
                      <FormattedMessage
                        id=""
                        description="Text for a link which redirects user to the Products page."
                        defaultMessage="Products" />
                    </Text>
                  </Button>
                  <Menu.Content {...getContentProps()}>
                    {/* This content is portaled and requires new rebrand theme context */}
                    <RebrandTheme>
                      <Menu.Segment
                        // Only show this label on HelloSign. This menu on
                        // HelloFax is simpler and the label is not necessary.
                        {...(isSign ? {
                          withLabel: (
                            <FormattedMessage
                              id=""
                              description="Heading for a list of products owned by us."
                              defaultMessage="Our products" />
                          ),
                        } : {})}
                      >
                        {isFax && (
                          <Menu.LinkItem
                            data-qa-ref="global-nav-esign-product-link"
                            href={HfReactHelper.urlHelper('home/bridge/to/S', HfReactHelper.HfSites.SITE_CODE_HF)}
                            target="_blank"
                          >
                            {/* TODO Make dynamic for rebrand */}
                            HelloSign
                          </Menu.LinkItem>
                        )}
                        <Menu.LinkItem
                          data-qa-ref="global-nav-esign-api-product-link"
                          href={HfReactHelper.getWebflowUrl('products/api')}
                          target="_blank"
                        >
                          <FormattedMessage
                            id=""
                            description="A link which redirects the user to the API page for the given product. Ex. 'HelloSign API'"
                            defaultMessage="{productName} API"
                            values={{
                              productName: 'HelloSign', // TODO make me dynamic for rebrand
                            }} />
                        </Menu.LinkItem>
                        <Menu.LinkItem
                          data-qa-ref="global-nav-esign-mobile-product-link"
                          href={HfReactHelper.getWebflowUrl('features/mobile')}
                          target="_blank"
                        >
                          <FormattedMessage
                            id=""
                            description="A link which redirects the user to the mobile app page for the given product. Ex. 'HelloSign mobile'"
                            defaultMessage="{productName} mobile"
                            values={{
                              productName: 'HelloSign', // TODO make me dynamic for rebrand
                            }} />
                        </Menu.LinkItem>
                        {isSign && (
                          <Menu.LinkItem
                            data-qa-ref="global-nav-fax-product-link"
                            href={HfReactHelper.urlHelper('home/bridge/to/F', HfReactHelper.HfSites.SITE_CODE_HS)}
                            target="_blank"
                          >
                            {/* TODO Make dynamic for rebrand */}
                            HelloFax
                          </Menu.LinkItem>
                        )}
                      </Menu.Segment>
                      {isSign && (
                        <Menu.Segment
                          withLabel={
                            <FormattedMessage
                              id=""
                              description="Heading for a list of integrations available for our product."
                              defaultMessage="Integrations" />
                          }
                        >
                          <Menu.LinkItem
                            data-qa-ref="global-nav-gmail-integration-link"
                            href={HfReactHelper.getWebflowUrl('integrations/gmail')}
                            target="_blank"
                          >
                            <FormattedMessage
                              id=""
                              description="A link which redirects the user to Gmail integration page."
                              defaultMessage="Gmail" />
                          </Menu.LinkItem>
                          <Menu.LinkItem
                            data-qa-ref="global-nav-gdocs-integration-link"
                            href={HfReactHelper.getWebflowUrl('integrations/google-docs')}
                            target="_blank"
                          >
                            <FormattedMessage
                              id=""
                              description="A link which redirects the user to Google Docs integration page."
                              defaultMessage="Google Docs" />
                          </Menu.LinkItem>
                          <Menu.LinkItem
                            data-qa-ref="global-nav-gdrive-integration-link"
                            href={HfReactHelper.getWebflowUrl('integrations/google-drive')}
                            target="_blank"
                          >
                            <FormattedMessage
                              id=""
                              description="A link which redirects the user to Google Drive integration page."
                              defaultMessage="Google Drive" />
                          </Menu.LinkItem>
                        </Menu.Segment>
                      )}
                    </RebrandTheme>
                  </Menu.Content>
                </>
              )}
            </Menu.Wrapper>
          </div>
          <div className={styles.footerItem}>
            <Menu.Wrapper closeOnSelection>
              {({ getContentProps, getTriggerProps }) => (
                <>
                  <Button
                    {...getTriggerProps()}
                    className={styles.navButton}
                    variant="transparent"
                    data-qa-ref="global-nav-privacy-legal-menu"
                    hasNoUnderline
                  >
                    <Text color="faint">
                      <FormattedMessage
                        id=""
                        description="Text for a link which redirects user to the Privacy and Legal page."
                        defaultMessage="Privacy and legal" />
                    </Text>
                  </Button>
                  <Menu.Content {...getContentProps()}>
                    {/* This content is portaled and requires new rebrand theme context */}
                    <RebrandTheme>
                      <Menu.LinkItem
                        data-qa-ref="global-nav-security-link"
                        href={HfReactHelper.getWebflowUrl('trust/security')}
                        target="_blank"
                      >
                        <FormattedMessage
                          id=""
                          description="Text for a link which redirects user to the website Security information page."
                          defaultMessage="Security" />
                      </Menu.LinkItem>
                      <Menu.LinkItem
                        data-qa-ref="global-nav-legal-link"
                        href={HfReactHelper.getWebflowUrl('legal')}
                        target="_blank"
                      >
                        <FormattedMessage
                          id=""
                          description="Text for a link which redirects user to the Legal information page."
                          defaultMessage="Legal" />
                      </Menu.LinkItem>
                      <Menu.LinkItem
                        data-qa-ref="global-nav-privacy-link"
                        href={HfReactHelper.getWebflowUrl('privacy')}
                        target="_blank"
                      >
                        <FormattedMessage
                          id=""
                          description="Text for a link which redirects user to the Privacy Policy page."
                          defaultMessage="Privacy" />
                      </Menu.LinkItem>
                      <Menu.LinkItem
                        data-qa-ref="global-nav-terms-link"
                        href={HfReactHelper.getWebflowUrl('terms')}
                        target="_blank"
                      >
                        <FormattedMessage
                          id=""
                          description="Text for a link which redirects user to the Terms and Conditions page."
                          defaultMessage="Terms" />
                      </Menu.LinkItem>
                    </RebrandTheme>
                  </Menu.Content>
                </>
              )}
            </Menu.Wrapper>
          </div>
          <div className={classnames(styles.footerItem, styles.socialLinks)}>
            {/* TODO make these dynamic for rebrand */}
            <a
              href="https://www.linkedin.com/company/hellosign/"
              rel="noreferrer nofollow"
              data-qa-ref="global-nav-linkedin-social-link"
              target="_blank"
            >
              <UIIcon color="faint" src={LinkedInLogo} />
            </a>
            <a
              href="https://www.facebook.com/hellosignapp"
              rel="noreferrer nofollow"
              data-qa-ref="global-nav-facebook-social-link"
              target="_blank"
            >
              <UIIcon color="faint" src={FacebookLogo} />
            </a>
            <a
              href="https://twitter.com/hellosign"
              rel="noreferrer nofollow"
              data-qa-ref="global-nav-twitter-social-link"
              target="_blank"
            >
              <UIIcon color="faint" src={TwitterLogo} />
            </a>
          </div>
        </div>
      </div>
    </ActiveItemContext.Provider>
  );
}

export default withRebrandTheme(GlobalNav);
